import React, {useState, useEffect, useMemo} from 'react';
import { LazyLoadImage } from 'react-lazy-load-image-component';

import PastilleDate from './../../components/PastilleDate/PastilleDate.js';
import {ReactComponent as Clock} from '../../assets/image/clock.svg';
import {ReactComponent as MapLinkIcon} from '../../assets/image/map_link.svg';
import {ReactComponent as PlusIcon} from '../../assets/image/plus_card.svg';
import {
    formatHeureQuebec,
    computeActivityCardText,
} from '../../utils/activityCardDisplay.js';

import './CardSubEventV3.scss';

const CROSSFADE_INTERVAL_MS = 4500;

function normalizeThumbSrc(url) {
    if (!url) return null;
    const s = String(url);
    return s.includes('|') ? s.split('|')[0] : s;
}

const CardSubEvent = (props) => {
    const {
        id = 0,
        heure_debut = false,
        heure_fin = false,
        date = 'TBA',
        thumbnail = null,
        thumbnail_credit = null,
        thumbnails: thumbnailsProp = null,
        titre = null,
        adresse = null,
        categorie = null,
        location_nicename = '',
        subCategorie = null,
        animation = null,
        animateur = null,
        releve = null,
        artiste = null,
        artistNames: artistNamesProp = null,
        chorale = null,
        representants = null,
        activeArtistFilter = null,
        lastClickedArtist = null,
        onOpenDetails = undefined,
    } = props;

    const day = date.split(' ')[0];

    const cardTextInput = useMemo(
        () => ({
            titre,
            animateur,
            subCategorie,
            heure_debut,
            artiste,
            artistNames: artistNamesProp,
            animation,
            chorale,
            releve,
            representants,
            categorie,
        }),
        [
            titre,
            animateur,
            subCategorie,
            heure_debut,
            artiste,
            artistNamesProp,
            animation,
            chorale,
            releve,
            representants,
            categorie,
        ],
    );

    const { mainTitle, subtitles, showSubCategorieRow } = useMemo(
        () => computeActivityCardText(cardTextInput),
        [cardTextInput],
    );

    const selectedArtists = useMemo(() => {
        if (!Array.isArray(activeArtistFilter) || activeArtistFilter.length === 0) return [];
        return activeArtistFilter.map((a) => String(a).trim()).filter(Boolean);
    }, [activeArtistFilter]);

    const imageList = useMemo(() => {
        if (Array.isArray(thumbnailsProp) && thumbnailsProp.length > 0) {
            return thumbnailsProp
                .map((t) => ({
                    src: normalizeThumbSrc(t.src),
                    credit: t.credit || null,
                    artists: Array.isArray(t.artists)
                        ? t.artists.map((a) => String(a).trim()).filter(Boolean)
                        : [],
                }))
                .filter((t) => t.src);
        }
        if (thumbnail) {
            return [
                {
                    src: normalizeThumbSrc(thumbnail),
                    credit: thumbnail_credit || null,
                    artists: [],
                },
            ].filter((t) => t.src);
        }
        return [];
    }, [thumbnailsProp, thumbnail, thumbnail_credit]);

    const multiImage = imageList.length > 1;

    const [activeIdx, setActiveIdx] = useState(0);
    const [reducedMotion, setReducedMotion] = useState(false);

    useEffect(() => {
        const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
        const apply = () => setReducedMotion(mq.matches);
        apply();
        mq.addEventListener('change', apply);
        return () => mq.removeEventListener('change', apply);
    }, []);

    const imageListKey = imageList.map((x) => x.src).join('|');
    const activeArtistFilterKey = selectedArtists.slice().sort().join('|');
    const priorityName =
        lastClickedArtist && String(lastClickedArtist).trim()
            ? String(lastClickedArtist).trim()
            : null;

    useEffect(() => {
        if (selectedArtists.length === 0 || imageList.length === 0) {
            setActiveIdx(0);
            return;
        }
        let matchIdx = -1;
        if (priorityName && selectedArtists.includes(priorityName)) {
            matchIdx = imageList.findIndex((slide) =>
                (slide.artists || []).includes(priorityName),
            );
        }
        if (matchIdx < 0) {
            matchIdx = imageList.findIndex((slide) =>
                (slide.artists || []).some((a) => selectedArtists.includes(a)),
            );
        }
        setActiveIdx(matchIdx >= 0 ? matchIdx : 0);
    }, [imageListKey, activeArtistFilterKey, priorityName, imageList, selectedArtists]);

    useEffect(() => {
        if (!multiImage || reducedMotion) return undefined;
        const timer = setInterval(() => {
            setActiveIdx((i) => (i + 1) % imageList.length);
        }, CROSSFADE_INTERVAL_MS);
        return () => clearInterval(timer);
    }, [multiImage, reducedMotion, imageList.length]);

    const displayIdx = multiImage && reducedMotion ? 0 : activeIdx;
    const activeCredit = imageList[displayIdx] ? imageList[displayIdx].credit : null;

    return (
        <div className="CardSubEventV3 card">
            <figure className="thumb">
                <div
                    className={
                        multiImage
                            ? 'thumb-stack thumb-stack--multi'
                            : 'thumb-stack thumb-stack--single'
                    }
                >
                    {imageList.length === 0 ? (
                        <div className="emptyThumb" />
                    ) : (
                        imageList.map((img, i) => (
                            <LazyLoadImage
                                key={`${id}-${img.src}-${i}`}
                                src={img.src}
                                alt={mainTitle || ''}
                                className={
                                    multiImage && !reducedMotion
                                        ? `thumb-layer${i === displayIdx ? ' is-active' : ''}`
                                        : 'thumb-layer thumb-layer--single'
                                }
                            />
                        ))
                    )}
                </div>
                {activeCredit ? (
                    <figcaption className="credit">{activeCredit}</figcaption>
                ) : (
                    ''
                )}
                <PastilleDate date={day} />
            </figure>
            <div className="card-body">
                <div className="card-body-text">
                    {categorie && categorie !== mainTitle ? (
                        <div className="categ">{categorie}</div>
                    ) : null}
                    <div className="card-body-titles">
                        <div className="card-title">
                            <h5>{mainTitle}</h5>
                        </div>
                        {subtitles.map((line, idx) => (
                            <div className="card-subtitle" key={`sub-${idx}`}>
                                {line}
                            </div>
                        ))}
                        {showSubCategorieRow ? (
                            <div className="categ2">{subCategorie}</div>
                        ) : null}
                    </div>
                </div>
                <div className="card-body-meta">
                    <div className="card-body-meta-left">
                        {heure_debut ? (
                            <div className="meta-row meta-row--time">
                                <span className="meta-row-icon" aria-hidden>
                                    <Clock />
                                </span>
                                <span className="meta-row-text meta-row-text--muted">
                                    {formatHeureQuebec(heure_debut)}
                                    {heure_fin
                                        ? `\u00a0à\u00a0${formatHeureQuebec(heure_fin)}`
                                        : ''}
                                </span>
                            </div>
                        ) : null}
                        {location_nicename || adresse ? (
                            <div className="meta-row meta-row--place">
                                <span className="meta-row-icon" aria-hidden>
                                    <MapLinkIcon />
                                </span>
                                <a
                                    className="meta-row-text meta-row-text--link"
                                    href={
                                        'https://www.google.com/maps/search/' +
                                        encodeURIComponent(adresse || '')
                                    }
                                    target="_blank"
                                    rel="noreferrer"
                                >
                                    {location_nicename || adresse}
                                </a>
                            </div>
                        ) : null}
                    </div>
                    <button
                        type="button"
                        className="card-body-expand"
                        aria-label="Voir plus de détails sur l'activité"
                        onClick={onOpenDetails}
                    >
                        <PlusIcon className="card-body-expand-icon" aria-hidden />
                    </button>
                </div>
            </div>
        </div>
    );
};

export default CardSubEvent;
